import { AudioBufferSource, Output, Mp3OutputFormat, BufferTarget, canEncodeAudio } from 'mediabunny';
import { registerMp3Encoder } from '@mediabunny/mp3-encoder';

/**
 * Detect supported audio format - prioritize WebCodec, fallback to MediaBunny MP3
 */
export async function detectSupportedAudioFormat(): Promise<'mp3' | 'unsupported'> {
  try {
    // Check native MP3 support first (WebCodec)
    const mp3Supported = await canEncodeAudio('mp3');
    if (mp3Supported) {
      console.log('Browser supports native MP3 encoding via WebCodec');
      return 'mp3';
    } else {
      // Register MP3 encoder extension for browsers without native support
      console.log('Registering MP3 encoder extension...');
      registerMp3Encoder();
      console.log('MP3 encoder registered successfully');
      return 'mp3';
    }
  } catch (error) {
    console.error('MP3 encoding setup failed:', error);
    return 'unsupported';
  }
}

/**
 * Convert AudioBuffer to MP3 using MediaBunny
 */
export async function convertAudioBufferToMP3(
  audioBuffer: AudioBuffer,
  onProgress?: (progress: number) => void
): Promise<Blob> {
  try {
    console.log('Starting MP3 encoding with ...', {
      duration: audioBuffer.duration,
      sampleRate: audioBuffer.sampleRate,
      numberOfChannels: audioBuffer.numberOfChannels
    });

    // Create audio source from the buffer
    const audioSource = new AudioBufferSource({
      codec: 'mp3',
      bitrate: 128000, // 128 kbps for good quality
    });

    // Create buffer target to receive the output
    const target = new BufferTarget();

    // Create output configuration for MP3
    const output = new Output({
      format: new Mp3OutputFormat(),
      target,
    });

    // Add audio track
    output.addAudioTrack(audioSource);

    // Track progress
    const startTime = performance.now();
    const audioDuration = audioBuffer.duration;
    let progressInterval: NodeJS.Timeout | null = null;

    // Report initial progress
    if (onProgress) {
      onProgress(0);
    }

    // Start the encoding process
    await output.start();

    // Set up progress tracking based on encoding speed
    const estimatedEncodingTime = (audioDuration * 1000) / 50; // Assume 50x real-time speed
    
    progressInterval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / estimatedEncodingTime, 0.95); // Cap at 95% until finalize
      
      if (onProgress) {
        onProgress(Math.round(progress * 100));
      }
    }, 50); // Update every 50ms for smooth progress

    // Add the AudioBuffer directly to the source
    await audioSource.add(audioBuffer);

    console.log('Finalizing output...');
    // Finalize the output
    await output.finalize();

    // Clear progress interval
    if (progressInterval) {
      clearInterval(progressInterval);
    }
    
    // Report completion
    if (onProgress) {
      onProgress(100);
    }

    // Get the result
    const resultBuffer = target.buffer;
    if (!resultBuffer) {
      throw new Error('No output buffer available after encoding');
    }
    
    const blob = new Blob([resultBuffer], { type: 'audio/mpeg' });

    const encodingTime = (performance.now() - startTime) / 1000;
    const speedFactor = audioDuration / encodingTime;
    
    console.log(`MP3 encoding complete: ${(blob.size / 1024 / 1024).toFixed(2)} MB (from ${(audioBuffer.length * 4 / 1024 / 1024).toFixed(2)} MB raw)`);
    console.log(`Encoding took ${encodingTime.toFixed(2)}s for ${audioDuration.toFixed(2)}s of audio (${speedFactor.toFixed(1)}x real-time)`);
    
    return blob;
  } catch (error) {
    console.error('Error encoding to MP3:', error);
    throw error;
  }
}

/**
 * Convert Blob to AudioBuffer using Web Audio API
 */
export async function blobToAudioBuffer(blob: Blob, audioContext: AudioContext): Promise<AudioBuffer> {
  const arrayBuffer = await blob.arrayBuffer();
  return await audioContext.decodeAudioData(arrayBuffer);
}